import { useState, useMemo } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  Divider,
  LinearProgress,
  Avatar,
  Skeleton,
  Alert,
} from "@mui/material"
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as BankIcon,
  Payment as PaymentIcon,
  Inbox as InboxIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"
import ReportGenerator from "./ReportGenerator"

// Reusable Metric Card with SaaS styling and hover animations
function MetricCard({ title, value, icon, bgColor, iconColor, subtitle }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: "1px solid #f1f5f9",
        // boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05), 0 1px 2px 0 rgba(15, 23, 42, 0.03)",
        // transition: "all 0.2s ease-in-out",
        backgroundColor: "#ffffff",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 5px 10px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.04)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="overline"
              sx={{ fontWeight: 600, letterSpacing: "0.05em", color: "#64748b" }}
            >
              {title}
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: "#0f172a", my: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              backgroundColor: bgColor,
              color: iconColor,
              width: 52,
              height: 52,
              borderRadius: "10px",
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  )
}

// Payment method progress bar row
function PaymentMethodRow({ method, total, count, maxAmount, totalCollection }) {
  const percentage = totalCollection > 0 ? Math.round((total / totalCollection) * 100) : 0
  const barValue = maxAmount > 0 ? (total / maxAmount) * 100 : 0
  const isBankTransfer = method?.toLowerCase() === "bank_transfer" || method?.toLowerCase() === "bank transfer"

  const getMethodThemeColor = (m) => {
    const colors = {
      cash: "#10b981", // green
      bank_transfer: "#3b82f6", // blue
      "bank transfer": "#3b82f6",
      cheque: "#06b6d4", // cyan
      upi: "#8b5cf6", // purple
      card: "#f59e0b", // amber
    }
    return colors[m?.toLowerCase()] || "#64748b"
  }

  const getPaymentMethodIcon = (m) => {
    const lowerMethod = m?.toLowerCase()
    switch (lowerMethod) {
      case "cash":
        return <MoneyIcon sx={{ fontSize: 16 }} />
      case "bank_transfer":
      case "bank transfer":
        return <BankIcon sx={{ fontSize: 16 }} />
      case "cheque":
        return <ReceiptIcon sx={{ fontSize: 16 }} />
      case "upi":
        return <PaymentIcon sx={{ fontSize: 16 }} />
      case "card":
        return <PaymentIcon sx={{ fontSize: 16 }} />
      default:
        return <PaymentIcon sx={{ fontSize: 16 }} />
    }
  }

  const themeColor = getMethodThemeColor(method)

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: themeColor, display: "flex", alignItems: "center" }}>
            {getPaymentMethodIcon(method)}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", textTransform: "capitalize" }}>
            {method.replace("_", " ")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
            ₹{total.toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            ({count} receipt{count !== 1 ? "s" : ""})
          </Typography>
        </Stack>
      </Box>
      <LinearProgress
        variant="determinate"
        value={barValue}
        sx={{
          height: 8,
          borderRadius: "4px",
          backgroundColor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: themeColor,
            borderRadius: "4px",
          },
        }}
      />
    </Box>
  )
}

// Compact Quick Action Row Link Button
function QuickActionRow({ label, icon, onClick }) {
  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={onClick}
      startIcon={icon}
      sx={{
        justifyContent: "flex-start",
        textTransform: "none",
        fontWeight: 600,
        color: "#334155",
        borderColor: "#e2e8f0",
        borderRadius: "8px",
        py: 1.25,
        px: 2,
        mb: 1.5,
        transition: "all 0.2s",
        backgroundColor: "#ffffff",
        "&:hover": {
          borderColor: "#cbd5e1",
          backgroundColor: "#f8fafc",
          transform: "translateX(2px)"
        },
      }}
    >
      {label}
    </Button>
  )
}

// Skeleton loading layout for FeeCollectorDashboard
function DashboardSkeleton() {
  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 4, gap: 2 }}>
        <Box>
          <Skeleton variant="text" width={240} height={40} />
          <Skeleton variant="text" width={320} height={20} />
        </Box>
        <Stack direction="row" spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
          <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: "8px" }} />
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2].map((i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Paper sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ width: "60%" }}>
                  <Skeleton variant="text" width="90%" height={16} />
                  <Skeleton variant="text" width="60%" height={36} />
                </Box>
                <Skeleton variant="rectangular" width={52} height={52} sx={{ borderRadius: "10px" }} />
              </Box>
              <Skeleton variant="text" width="40%" height={16} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
            <Skeleton variant="text" width="30%" height={28} sx={{ mb: 3 }} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Skeleton variant="text" width="25%" height={20} />
                  <Skeleton variant="rectangular" width={90} height={20} sx={{ borderRadius: "10px" }} />
                </Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="40%" height={14} />
                {i < 3 && <Divider sx={{ mt: 2.5 }} />}
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none", mb: 3 }}>
            <Skeleton variant="text" width="55%" height={28} sx={{ mb: 3 }} />
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton variant="text" width="20%" height={18} />
                </Box>
                <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: "4px" }} />
              </Box>
            ))}
          </Paper>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
            <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px", mb: 1.5 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px", mb: 1.5 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px" }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

function FeeCollectorDashboard() {
  const navigate = useNavigate()
  const [dateFilter, setDateFilter] = useState("today")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  // Fetch all receipts
  const { data: receipts, isLoading: receiptsLoading, error: receiptsError, refetch } = useApiQuery(
    ["receipts"],
    "/receipts"
  )

  // Helper function to filter receipts by date range selected
  const getFilteredReceipts = useMemo(() => {
    if (!receipts) return []

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

    return receipts.filter((receipt) => {
      const receiptDate = new Date(receipt.collectedDate)

      switch (dateFilter) {
        case "today":
          return receiptDate >= today
        case "30days":
          return receiptDate >= thirtyDaysAgo
        case "month":
          return receiptDate >= monthStart
        case "all":
        default:
          return true
      }
    })
  }, [receipts, dateFilter])

  // Calculate statistics from filtered receipts
  const stats = useMemo(() => {
    const filtered = getFilteredReceipts
    const totalCollected = filtered.reduce((sum, receipt) => sum + (receipt.amount || 0), 0)
    const receiptsCount = filtered.length

    return {
      totalCollected,
      receiptsAdded: receiptsCount,
    }
  }, [getFilteredReceipts])

  // Calculate payment method summary
  const paymentMethodSummary = useMemo(() => {
    const filtered = getFilteredReceipts
    const summary = {}

    filtered.forEach((receipt) => {
      const method = receipt.paymentMethod?.toLowerCase() || "unknown"

      if (!summary[method]) {
        summary[method] = {
          count: 0,
          total: 0,
          receipts: [],
          banks: {}
        }
      }

      summary[method].count += 1
      summary[method].total += receipt.amount || 0
      summary[method].receipts.push(receipt)

      if (method === "bank_transfer" || method === "bank transfer") {
        const bankName = receipt.bank || "Unknown Bank"
        if (!summary[method].banks[bankName]) {
          summary[method].banks[bankName] = {
            count: 0,
            total: 0,
          }
        }
        summary[method].banks[bankName].count += 1
        summary[method].banks[bankName].total += receipt.amount || 0
      }
    })

    return Object.entries(summary)
      .map(([method, data]) => ({
        method,
        ...data,
      }))
      .sort((a, b) => b.total - a.total)
  }, [getFilteredReceipts])

  // Find max payment method total for scaling progress bars
  const maxPaymentMethodAmount = useMemo(() => {
    if (paymentMethodSummary.length === 0) return 0
    return Math.max(...paymentMethodSummary.map(m => m.total))
  }, [paymentMethodSummary])

  // Get recent receipts (last 10)
  const recentReceipts = useMemo(() => {
    return [...getFilteredReceipts]
      .sort((a, b) => new Date(b.collectedDate).getTime() - new Date(a.collectedDate).getTime())
      .slice(0, 10)
  }, [getFilteredReceipts])

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      cash: "primary",
      "bank transfer": "success",
      bank_transfer: "success",
      cheque: "info",
      upi: "secondary",
      card: "warning",
    }
    return methodColors[method?.toLowerCase()] || "default"
  }

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  const getClientName = (receipt) => {
    if (typeof receipt.clientId === "object" && receipt.clientId?.name) {
      return receipt.clientId.name
    }
    return receipt.clientName || "Unknown Client"
  }

  if (receiptsLoading) return <DashboardSkeleton />

  if (receiptsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading dashboard data: {receiptsError.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ py: 1, minHeight: "90vh" }}>
      {/* Top Actionable Header Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
            Fee Collection
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            Track collections, manage receipt entries, and review active billing pipelines.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {/* Integrated Filter Control */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 1.5,
              height: 40,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.02)",
            }}
          >
            <CalendarIcon sx={{ color: "#94a3b8", fontSize: 18, mr: 1 }} />
            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, mr: 1.5, display: { xs: "none", sm: "inline" } }}>
              Filter:
            </Typography>
            <FormControl variant="standard" size="small">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                disableUnderline
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  cursor: "pointer",
                  "& .MuiSelect-select": {
                    py: 0,
                    pr: 3,
                  },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ flexGrow: { xs: 1, sm: 0 } }}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={() => refetch()}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  height: 40,
                  width: 40,
                  color: "#475569",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/add-fee-receipts")}
              sx={{
                backgroundColor: "#fcd34d",
                color: "#1e293b",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                height: 40,
                boxShadow: "0 1px 2px 0 rgba(252, 211, 77, 0.3)",
                "&:hover": {
                  backgroundColor: "#fbbf24",
                  boxShadow: "none",
                },
              }}
            >
              Add Fee Receipt
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* KPI Stats Cards Area */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <MetricCard
            title="Total Collected"
            value={`₹${stats.totalCollected.toLocaleString()}`}
            icon={<MoneyIcon sx={{ fontSize: 26 }} />}
            bgColor="#dcfce7"
            iconColor="#15803d"
            subtitle={`${getFilterLabel()} settlements`}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard
            title="Receipts Added"
            value={stats.receiptsAdded}
            icon={<ReceiptIcon sx={{ fontSize: 26 }} />}
            bgColor="#e0f2fe"
            iconColor="#0369a1"
            subtitle={`${getFilterLabel()} logs`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column: Recent Fee Receipts Table */}
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
              backgroundColor: "#ffffff",
              minHeight: 480,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
                Recent Collections
              </Typography>
              <Button
                size="small"
                onClick={() => navigate("/all-receipts")}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontWeight: 600, color: "#3b82f6" }}
              >
                View All
              </Button>
            </Box>

            {recentReceipts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                  py: 6,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <Avatar
                  sx={{
                    backgroundColor: "#f8fafc",
                    color: "#94a3b8",
                    width: 72,
                    height: 72,
                    mb: 2,
                  }}
                >
                  <InboxIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}>
                  No collections found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 320, mb: 3 }}>
                  No fee receipts were recorded during the selected period.
                </Typography>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/add-fee-receipts")}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#3b82f6",
                    borderColor: "#e2e8f0",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  Add Fee Receipt
                </Button>
              </Box>
            ) : (
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Receipt No.</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Client</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Amount</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentReceipts.map((receipt) => (
                      <TableRow
                        key={receipt._id || receipt.id}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          cursor: "pointer",
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: "#334155" }}>
                            {receipt.receiptNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                            {getClientName(receipt)}
                          </Typography>
                          {receipt.clientId?.phoneNumber && (
                            <Typography variant="caption" color="textSecondary">
                              {receipt.clientId.phoneNumber}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#16a34a" }}>
                            ₹{(receipt.amount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={receipt.paymentMethod?.toUpperCase()}
                            color={getPaymentMethodColor(receipt.paymentMethod)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: 20,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {new Date(receipt.collectedDate).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            {new Date(receipt.collectedDate).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Right Column: Payment Breakdown & Quick Actions */}
        <Grid item xs={12} md={5}>
          {/* Payment Breakdown Card */}
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
              backgroundColor: "#ffffff",
              mb: 3,
              minHeight: 260,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
              Payment Methods
            </Typography>

            {paymentMethodSummary.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexGrow: 1, py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  No payment method distribution to display.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1 }}>
                {paymentMethodSummary.map((summary) => (
                  <PaymentMethodRow
                    key={summary.method}
                    method={summary.method}
                    total={summary.total}
                    count={summary.count}
                    maxAmount={maxPaymentMethodAmount}
                    totalCollection={stats.totalCollected}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Quick Actions Card */}
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
              backgroundColor: "#ffffff",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
              Quick Actions
            </Typography>

            <Box>
              <QuickActionRow
                label="View All Receipts"
                icon={<ReceiptIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/all-receipts")}
              />
              <QuickActionRow
                label="Add Fee Receipt"
                icon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/add-fee-receipts")}
              />
              <QuickActionRow
                label="Collection Report"
                icon={<AssessmentIcon sx={{ fontSize: 18 }} />}
                onClick={() => setReportDialogOpen(true)}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Report Generator Dialog */}
      <ReportGenerator open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />
    </Box>
  )
}

export default FeeCollectorDashboard